import React, {
  useMemo,
  useState,
  useEffect,
  useRef,
  Dispatch,
  SetStateAction,
} from 'react'
import {
  Button,
  SelectInput,
  TextInput,
  ToggleInput,
  RadioInput,
  DualSelect,
  DateRangePicker,
} from '@demandlocal/components'
import { FieldValues, FormProvider, useForm } from 'react-hook-form'
import { DateRange } from '../../lib/types/DateRange'
import dayjs from 'dayjs'
import { ReactComponent as SuccessIcon } from '../../images/icons/check_fill.svg'
import { AnimatePresence, motion } from 'framer-motion'
import { useSelector } from 'react-redux'
import { currentUserSelector } from '../../store/selectors/userSelector'
import { toast } from 'react-hot-toast'
import { useGetTagCodes } from '../../api/utils/useGetTagCodes'
import { useGetAgencyProfileActiveClients } from '../../api/agencies/useGetAgencyProfileActiveClients'
import useCheckSpreadsheetAccess from '../../api/dealership/utils/useCheckSpreadsheetAccess'
import { useParams } from 'react-router'
import { ReactComponent as RemoveIcon } from '../../images/icons/remove.svg'
import { usePostAgencyReportGenerator } from '../../api/agencies/reports/usePostAgencyReportGenerator'
import { getDayOrdinal } from '../../lib/utils/getDayOrdinal'
import { queryClient } from '../../api/queryClient'
import { useGetSimpleCampaignsList } from '../../api/agencies/useGetSimpleCampaignsList'
import { useGetAgencyExportById } from '../../api/agencies/reports/useGetAgencyExportById'

export const DIMENSIONS = [
  { label: 'Date', value: 'date' },
  { label: 'Booking Month', value: 'booking_month' },
  { label: 'Agency', value: 'agency_name' },
  { label: 'Client', value: 'client_name' },
  { label: 'Campaign', value: 'campaign_name' },
  { label: 'Campaign ID', value: 'campaign_id' },
  { label: 'Product', value: 'product_name' },
  { label: 'Start Date', value: 'start_date' },
  { label: 'End Date', value: 'end_date' },
]

export const METRICS = [
  { label: 'Impressions', value: 'impressions' },
  { label: 'Clicks', value: 'clicks' },
  { label: 'CTR', value: 'ctr' },
  { label: 'RSVs', value: 'rsv' },
  { label: 'Budget', value: 'budget' },
  { label: 'Booked Impressions', value: 'booked_impressions' },
  { label: 'Percent Delivered', value: 'percent_delivered' },
  { label: '25% Completion', value: '25_completion' },
  { label: '50% Completion', value: '50_completion' },
  { label: '75% Completion', value: '75_completion' },
  { label: 'Audio/Video Completions', value: 'video_completions' },
  { label: 'Phone Calls', value: 'phone_calls' },
]

const periodOptions = [
  { label: 'Last 7 days', value: 'last_7_days' },
  { label: 'Month to Date', value: 'month_to_date' },
  { label: 'Last Month', value: 'last_month' },
  { label: 'Last 3 months', value: 'last_3_months' },
  { label: 'Last 6 months', value: 'last_6_months' },
  { label: 'Year to date', value: 'year_to_date' },
  { label: 'Last year', value: 'last_year' },
  { label: 'Custom', value: 'custom' },
]

type FormValues = {
  name: string
  email: string
  dealerships: Array<{
    label: string
    value: string
  }>
  campaigns: Array<{
    label: string
    value: string
  }>
  all_dealerships: boolean
  all_campaigns: boolean
  all_dimensions: boolean
  all_metrics: boolean
  all_conversions: boolean
  dimensions?: Array<{
    label: string
    value: string
  }>
  metrics?: Array<{
    label: string
    value: string
  }>
  conversions?: Array<{
    label: string
    value: string
  }>
  recurring: boolean
  schedule_frequency: string | null
  schedule_day: {
    label: string
    value: number
  } | null
  reportFormat: string
  sheet_url: string
  period: {
    label: string
    value: string
  }
  customPeriod?: DateRange
}

export default function AgencyReportExportForm({
  export_id,
  setExpanded,
}: {
  export_id?: string
  setExpanded: Dispatch<SetStateAction<boolean>>
}): JSX.Element {
  const { agency_id } = useParams()
  const { mutateAsync: createReport } = usePostAgencyReportGenerator()
  const user = useSelector(currentUserSelector)
  const { data: conversions } = useGetTagCodes({ pageSize: 100 })
  const service_email = 'link-one-export@demandlocal-link.iam.gserviceaccount.com'
  const [defaultValues, setDefaultValues] = useState(true)

  const { data: currentExport } = useGetAgencyExportById({
    id: export_id,
    filters: {
      agency_id,
    },
  })

  const conversionsOptions = useMemo(() => {
    return conversions?.data?.map((d) => ({
      label: d.tag_name,
      value: `conv_${d.tag_code.toLowerCase()}`,
    }))
  }, [conversions?.data])

  const formContext = useForm<FormValues>({
    defaultValues: {
      all_dimensions: false,
      all_metrics: false,
      all_conversions: false,
      all_dealerships: true,
      all_campaigns: true,
      email: user?.email,
    },
  })

  const [filters, setFilters] = useState<DateRange>({
    dateRange: {
      startDate: dayjs().startOf('month').subtract(3, 'month').format('YYYY-MM-DD'),
      endDate: dayjs()
        .startOf('month')
        .subtract(1, 'month')
        .endOf('month')
        .format('YYYY-MM-DD'),
    },
  })

  const { data: dealerships } = useGetAgencyProfileActiveClients({
    pageIndex: 1,
    pageSize: 300,
    filters: {
      agency_id: agency_id,
    },
  })

  const dealershipOptions = useMemo(() => {
    return (
      dealerships?.data?.map((d) => ({
        label: d.dealership,
        value: d.id?.toString(),
      })) || []
    )
  }, [dealerships?.data])

  const {
    handleSubmit,
    watch,
    setValue,
    getValues,
    reset,
    formState: { isSubmitting, isSubmitSuccessful },
  } = formContext

  const allDealerships = watch('all_dealerships')
  const allCampaigns = watch('all_campaigns')
  const allDimensions = watch('all_dimensions')
  const allMetrics = watch('all_metrics')
  const allConversions = watch('all_conversions')

  const selectedDealerships = watch('dealerships')
  const selectedCampaigns = watch('campaigns')
  const selectedDimensions = watch('dimensions')
  const selectedMetrics = watch('metrics')
  const selectedConversions = watch('conversions')

  const recurring = watch('recurring')
  const scheduleFrequency = watch('schedule_frequency')
  const reportFormat = watch('reportFormat')
  const sheetURL = watch('sheet_url')
  const periodValue = watch('period')

  // Fetch campaigns based on selected dealerships
  const { data: campaigns, isLoading: isLoadingCampaigns } = useGetSimpleCampaignsList({
    filters: {
      agency_id,
      dealership_id: selectedDealerships || [],
    },
  })

  const uniqueCampaigns = useMemo(() => {
    const groupedMap = new Map()
    campaigns?.data?.forEach((obj) => {
      const key = obj.client_campaign_id
      if (!groupedMap.has(key)) {
        groupedMap.set(key, {
          label: obj.client_campaign_display_name,
          value: key,
          customDetail: obj.dealership,
        })
      }
    })
    return Array.from(groupedMap.values())
  }, [campaigns])

  const { mutateAsync: checkAccess } = useCheckSpreadsheetAccess()

  // Update all dealerships when toggle is changed
  useEffect(() => {
    if (allDealerships && dealershipOptions.length > 0)
      setValue('dealerships', dealershipOptions)
    if (!allDealerships) setValue('dealerships', [])
  }, [allDealerships, dealershipOptions, setValue])

  // Update all campaigns when toggle is changed
  useEffect(() => {
    if (allCampaigns && uniqueCampaigns.length > 0) setValue('campaigns', uniqueCampaigns)
    if (!allCampaigns) setValue('campaigns', [])
  }, [allCampaigns, uniqueCampaigns, setValue])

  // Update all dimensions when toggle is changed
  useEffect(() => {
    if (allDimensions) setValue('dimensions', DIMENSIONS)
  }, [setValue, allDimensions])

  // Update all dimensions when toggle is changed
  useEffect(() => {
    if (allMetrics) setValue('metrics', METRICS)
  }, [setValue, allMetrics])

  // Update all dimensions when toggle is changed
  useEffect(() => {
    if (allConversions) setValue('conversions', conversionsOptions)
  }, [setValue, allConversions, conversionsOptions])

  const scheduleOptions = useMemo(
    () => [
      {
        label: 'Day',
        value: 'daily',
      },
      {
        label: 'Month',
        value: 'monthly',
      },
    ],
    [],
  )

  const scheduleDayOptions = useMemo(
    () =>
      Array.from({ length: 31 }, (_, i) => ({
        label: `${i + 1}${getDayOrdinal(i + 1)}`,
        value: i + 1,
      })),
    [],
  )

  const onSubmit = async (values: Partial<FieldValues>): Promise<void> => {
    if (values?.reportFormat === 'google_sheet') {
      const googleAccessToast = toast.loading('Checking access...')

      await checkAccess(
        {
          spreadsheet_url: sheetURL,
          service_email: service_email,
        },
        {
          onSuccess: () => {
            toast.success('Access granted!', { id: googleAccessToast })
          },
          onError: () => {
            toast.error('Access denied!', { id: googleAccessToast })
          },
        },
      )
    }

    const toastId = toast.loading('Generating report...')
    await createReport(
      {
        agency_id: Number(agency_id),
        data: { ...values, service_email, export_id },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries([
            'agencies',
            'exports',
            { pageIndex: 1, pageSize: 20, queryParams: { agency_id }, sortingParams: '' },
          ])
        },
      },
    )
    toast.success('Report sent', { id: toastId })
  }

  useEffect(() => {
    // Only reset the form when export_id is provided and currentExport data is available
    // And either it's the first time or export_id has changed
    if (export_id && currentExport && defaultValues) {
      const defaultValues = {
        name: currentExport?.data?.name,
        period: periodOptions?.find(
          (period) => period.value === currentExport?.data?.period,
        ),
        customPeriod: { dateRange: currentExport?.data?.customPeriod },
        all_dealerships: currentExport?.data?.all_dealerships,
        all_campaigns: currentExport?.data?.all_campaigns,
        all_dimensions: DIMENSIONS.length === currentExport?.data?.dimensions?.length,
        all_metrics: METRICS.length === currentExport?.data?.metrics?.length,
        all_conversions:
          conversionsOptions?.length === currentExport?.data?.conversions?.length,
        dealerships: dealershipOptions?.filter((d) =>
          currentExport?.data?.dealerships?.includes(d.value?.toString()),
        ),
        campaigns: uniqueCampaigns?.filter((d) =>
          currentExport?.data?.campaigns?.includes(d.value),
        ),
        dimensions: DIMENSIONS?.filter((d) =>
          currentExport?.data?.dimensions?.includes(d.value),
        ),
        metrics: METRICS?.filter((d) => currentExport?.data?.metrics?.includes(d.value)),
        conversions: conversionsOptions?.filter((d) =>
          currentExport?.data?.conversions?.includes(d.value),
        ),
        recurring: currentExport?.recurring || false,
        schedule_frequency: scheduleOptions?.find(
          (opt) => opt.value === currentExport?.recurring_interval?.type,
        )?.value,
        schedule_day: scheduleDayOptions?.find(
          (opt) => opt.value === currentExport?.recurring_interval?.day,
        ),
        email: currentExport?.data?.email?.[0]?.to,
        reportFormat: currentExport?.data?.reportFormat,
        sheet_url: currentExport?.data?.google_sheet_url,
      }

      if (currentExport?.data?.customPeriod)
        setFilters({ dateRange: currentExport?.data?.customPeriod })

      reset(defaultValues)
      if (
        Number(dealershipOptions?.length) > 0 &&
        Number(uniqueCampaigns?.length) > 0 &&
        Number(conversionsOptions?.length) > 0
      )
        setDefaultValues(false)
    }
  }, [
    reset,
    currentExport,
    export_id,
    conversionsOptions,
    scheduleOptions,
    scheduleDayOptions,
    defaultValues,
    dealershipOptions,
    uniqueCampaigns,
  ])

  return (
    <>
      <FormProvider {...formContext}>
        <AnimatePresence>
          <motion.form
            animate={'open'}
            initial={'collapsed'}
            exit={'collapsed'}
            transition={{ type: 'spring', mass: 1, damping: 50, stiffness: 500 }}
            variants={{
              open: {
                opacity: 1,
                height: 'auto',
              },
              collapsed: {
                opacity: 0,
                height: 0,
              },
            }}
            onSubmit={handleSubmit(onSubmit)}>
            <div
              className={
                'p-5 relative rounded-md border-2 bg-primary-persian-blue/5 border-primary-persian-blue/10 mb-8'
              }>
              {isSubmitSuccessful && (
                <div
                  className={
                    'h-96 w-full mx-auto flex flex-col items-center justify-center'
                  }>
                  <SuccessIcon />
                  <div className={'pt-5 font-bold text-[28px]'}>
                    Great! Your agency report is now being generated.
                  </div>
                  <div className={'pt-3 font-medium text-base'}>
                    You will receive an email with the report shortly.
                  </div>
                  <div className={'pt-4 flex flex-row gap-2'}>
                    <Button
                      variant={'tertiary'}
                      type={'button'}
                      onClick={(e) => {
                        e.preventDefault()
                        reset()
                        setExpanded(false)
                      }}>
                      Close
                    </Button>
                    <Button
                      variant={'rainbow'}
                      onClick={(e) => {
                        e.preventDefault()
                        reset()
                      }}>
                      Generate another report
                    </Button>
                  </div>
                </div>
              )}
              {isSubmitting && (
                <div className={'w-full h-full absolute top-0 left-0 bg-white/50 z-20'} />
              )}
              {!isSubmitSuccessful && (
                <div>
                  <div
                    className={
                      'flex flex-row justify-between items-center border-b-2 pb-5 border-primary-persian-blue/10'
                    }>
                    <h3 className={'font-bold text-primary-xiketic text-lg'}>
                      Agency report generator
                    </h3>
                  </div>

                  {/* Period Selection */}
                  <div
                    className={
                      'flex flex-col gap-3 py-5 border-b-2 border-primary-persian-blue/10'
                    }>
                    <div className="flex flex-row justify-between items-center">
                      <div className="flex flex-row gap-4 w-1/2 items-center">
                        <TextInput
                          name="name"
                          label={'Export Name'}
                          rules={{ required: true }}
                        />
                        <div className="w-fit">
                          <SelectInput
                            required
                            label="Reporting Period"
                            name="period"
                            options={periodOptions}
                            onChange={(e) => {
                              if (e.value === 'custom') setValue('customPeriod', filters)
                            }}
                          />
                        </div>
                      </div>
                      {periodValue?.value === 'custom' && (
                        <div className="flex flex-row items-center gap-3">
                          <h1 className="font-bold text-sm">Select custom date range</h1>
                          <DateRangePicker
                            filters={filters}
                            onChange={(e) => {
                              setFilters(e)
                              setValue('customPeriod', e)
                            }}
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Dealership Selection */}
                  <div
                    className={
                      'flex flex-col gap-3 py-5 border-b-2 border-primary-persian-blue/10'
                    }>
                    <div className={'flex flex-row justify-between'}>
                      <h4 className={'font-bold text-base text-primary-xiketic'}>
                        Dealerships
                      </h4>
                      <div className={'flex flex-row gap-2 items-center '}>
                        <ToggleInput
                          trackClassName={'bg-primary-persian-blue/10'}
                          name={'all_dealerships'}
                          label={'All Dealerships'}
                        />
                      </div>
                    </div>
                    {!allDealerships && (
                      <div className="w-full flex flex-row gap-2">
                        <DualSelect
                          name="dealerships"
                          options={dealershipOptions as any[]}
                          buttonTitle={'Select dealerships'}
                          optionsListTitle={'Dealerships'}
                          selectedOptionsListTitle={'Selected dealerships'}
                        />
                        {selectedDealerships?.length > 0 && (
                          <motion.div
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                            className={
                              'pl-2 flex flex-row flex-1 flex-wrap justify-start items-end gap-3 border-l-2'
                            }>
                            {selectedDealerships?.map(
                              (
                                value: { value: string; label: string },
                                index: number,
                              ) => {
                                return (
                                  <div
                                    key={value.value}
                                    className={
                                      'flex flex-col p-2 bg-white border-2 min-w-32 rounded-lg min-w-[120px]'
                                    }>
                                    <div className={'flex justify-between text-xs'}>
                                      <span className={'font-bold'}>{value.label}</span>
                                      <RemoveIcon
                                        className={'cursor-pointer ml-2'}
                                        onClick={(e) => {
                                          e.preventDefault()
                                          setValue(
                                            `dealerships`,
                                            getValues(`dealerships`).filter(
                                              (v: { value: string }) =>
                                                v.value !== value.value,
                                            ),
                                          )
                                        }}
                                      />
                                    </div>
                                    <span className={'font-medium pt-1 text-xs'}>
                                      {value.value}
                                    </span>
                                  </div>
                                )
                              },
                            )}
                          </motion.div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Campaign Selection */}
                  <div
                    className={
                      'flex flex-col gap-3 py-5 border-b-2 border-primary-persian-blue/10'
                    }>
                    <div className={'flex flex-row justify-between'}>
                      <h4 className={'font-bold text-base text-primary-xiketic'}>
                        Campaigns
                      </h4>
                      <div className={'flex flex-row gap-2 items-center '}>
                        <ToggleInput
                          trackClassName={'bg-primary-persian-blue/10'}
                          name={'all_campaigns'}
                          label={'All Campaigns'}
                        />
                      </div>
                    </div>
                    {!allCampaigns && (
                      <div className="w-full flex flex-row gap-2">
                        <DualSelect
                          name="campaigns"
                          options={uniqueCampaigns as any[]}
                          buttonTitle={'Select campaigns'}
                          optionsListTitle={'Campaigns'}
                          selectedOptionsListTitle={'Selected campaigns'}
                          customOptionDetails={'customDetail'}
                        />
                        {selectedCampaigns?.length > 0 && (
                          <motion.div
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                            className={
                              'pl-2 flex flex-row flex-1 flex-wrap justify-start items-end gap-3 border-l-2'
                            }>
                            {selectedCampaigns?.map(
                              (
                                value: {
                                  value: string
                                  label: string
                                  customDetail?: string
                                },
                                index: number,
                              ) => {
                                return (
                                  <div
                                    key={value.value}
                                    className={
                                      'flex flex-col p-2 bg-white border-2 min-w-32 rounded-lg min-w-[120px]'
                                    }>
                                    <div className={'flex justify-between text-xs'}>
                                      <span className={'font-bold'}>{value.label}</span>
                                      <RemoveIcon
                                        className={'cursor-pointer ml-2'}
                                        onClick={(e) => {
                                          e.preventDefault()
                                          setValue(
                                            `campaigns`,
                                            getValues(`campaigns`).filter(
                                              (v: { value: string }) =>
                                                v.value !== value.value,
                                            ),
                                          )
                                        }}
                                      />
                                    </div>
                                    <span className={'font-medium pt-1 text-xs'}>
                                      {value?.customDetail ?? value?.value}
                                    </span>
                                  </div>
                                )
                              },
                            )}
                          </motion.div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Dimensions */}
                  <div
                    className={
                      'flex flex-col gap-3 py-5 border-b-2 border-primary-persian-blue/10'
                    }>
                    <div className={'flex flex-row justify-between'}>
                      <h4 className={'font-bold text-base text-primary-xiketic'}>
                        Dimensions
                      </h4>
                      <div className={'flex flex-row gap-2 items-center '}>
                        <ToggleInput
                          trackClassName={'bg-primary-persian-blue/10'}
                          name={'all_dimensions'}
                          label={'All Dimensions'}
                        />
                      </div>
                    </div>
                    {!allDimensions && (
                      <div className="w-full flex flex-row gap-2">
                        <DualSelect
                          name="dimensions"
                          options={DIMENSIONS as any[]}
                          buttonTitle={'Select dimensions'}
                          optionsListTitle={'Dimensions'}
                          selectedOptionsListTitle={'Selected dimensions'}
                          showValue={false}
                        />
                        {Number(selectedDimensions?.length ?? 0) > 0 && (
                          <motion.div
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                            className={
                              'pl-2 flex flex-row flex-1 flex-wrap justify-start items-end gap-3 border-l-2'
                            }>
                            {selectedDimensions?.map(
                              (
                                value: { value: string; label: string },
                                index: number,
                              ) => {
                                return (
                                  <div
                                    key={value.value}
                                    className={
                                      'flex flex-col p-2 bg-white border-2 min-w-32 rounded-lg min-w-[120px]'
                                    }>
                                    <div className={'flex justify-between text-xs'}>
                                      <span className={'font-bold'}>{value.label}</span>
                                      <RemoveIcon
                                        className={'cursor-pointer ml-2'}
                                        onClick={(e) => {
                                          e.preventDefault()
                                          setValue(
                                            `dimensions`,
                                            getValues(`dimensions`)?.filter(
                                              (v: { value: string }) =>
                                                v.value !== value.value,
                                            ),
                                          )
                                        }}
                                      />
                                    </div>
                                  </div>
                                )
                              },
                            )}
                          </motion.div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Metrics */}
                  <div
                    className={
                      'flex flex-col gap-3 py-5 border-b-2 border-primary-persian-blue/10'
                    }>
                    <div className={'flex flex-row justify-between'}>
                      <h4 className={'font-bold text-base text-primary-xiketic'}>
                        Metrics
                      </h4>
                      <div className={'flex flex-row gap-2 items-center '}>
                        <ToggleInput
                          trackClassName={'bg-primary-persian-blue/10'}
                          name={'all_metrics'}
                          label={'All Metrics'}
                        />
                      </div>
                    </div>
                    {!allMetrics && (
                      <div className="w-full flex flex-row gap-2">
                        <DualSelect
                          name="metrics"
                          options={METRICS as any[]}
                          buttonTitle={'Select metrics'}
                          optionsListTitle={'Metrics'}
                          selectedOptionsListTitle={'Selected metrics'}
                          showValue={false}
                        />
                        {Number(selectedMetrics?.length ?? 0) > 0 && (
                          <motion.div
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                            className={
                              'pl-2 flex flex-row flex-1 flex-wrap justify-start items-end gap-3 border-l-2'
                            }>
                            {selectedMetrics?.map(
                              (
                                value: { value: string; label: string },
                                index: number,
                              ) => {
                                return (
                                  <div
                                    key={value.value}
                                    className={
                                      'flex flex-col p-2 bg-white border-2 min-w-32 rounded-lg min-w-[120px]'
                                    }>
                                    <div className={'flex justify-between text-xs'}>
                                      <span className={'font-bold'}>{value.label}</span>
                                      <RemoveIcon
                                        className={'cursor-pointer ml-2'}
                                        onClick={(e) => {
                                          e.preventDefault()
                                          setValue(
                                            `metrics`,
                                            getValues(`metrics`)?.filter(
                                              (v: { value: string }) =>
                                                v.value !== value.value,
                                            ),
                                          )
                                        }}
                                      />
                                    </div>
                                  </div>
                                )
                              },
                            )}
                          </motion.div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Conversions */}
                  <div
                    className={
                      'flex flex-col gap-3 py-5 border-b-2 border-primary-persian-blue/10'
                    }>
                    <div className={'flex flex-row justify-between'}>
                      <h4 className={'font-bold text-base text-primary-xiketic'}>
                        Conversions
                      </h4>
                      <div className={'flex flex-row gap-2 items-center '}>
                        <ToggleInput
                          trackClassName={'bg-primary-persian-blue/10'}
                          name={'all_conversions'}
                          label={'All Conversions'}
                        />
                      </div>
                    </div>
                    {!allConversions && (
                      <div className="w-full flex flex-row gap-2">
                        <DualSelect
                          name="conversions"
                          options={conversionsOptions as any[]}
                          buttonTitle={'Select conversions'}
                          optionsListTitle={'Conversions'}
                          selectedOptionsListTitle={'Selected conversions'}
                          showValue={false}
                        />
                        {Number(selectedConversions?.length ?? 0) > 0 && (
                          <motion.div
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                            className={
                              'pl-2 flex flex-row flex-1 flex-wrap justify-start items-end gap-3 border-l-2'
                            }>
                            {selectedConversions?.map(
                              (
                                value: { value: string; label: string },
                                index: number,
                              ) => {
                                return (
                                  <div
                                    key={value.value}
                                    className={
                                      'flex flex-col p-2 bg-white border-2 min-w-32 rounded-lg min-w-[120px]'
                                    }>
                                    <div className={'flex justify-between text-xs'}>
                                      <span className={'font-bold'}>{value.label}</span>
                                      <RemoveIcon
                                        className={'cursor-pointer ml-2'}
                                        onClick={(e) => {
                                          e.preventDefault()
                                          setValue(
                                            `conversions`,
                                            getValues(`conversions`)?.filter(
                                              (v: { value: string }) =>
                                                v.value !== value.value,
                                            ),
                                          )
                                        }}
                                      />
                                    </div>
                                  </div>
                                )
                              },
                            )}
                          </motion.div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Report Delivery */}
                  <div className={'flex flex-col pt-5 gap-4'}>
                    <div className={'flex flex-row justify-between items-center'}>
                      <h2 className={'font-bold text-primary-xiketic'}>Send it to</h2>
                      <div>
                        <ToggleInput
                          name={'recurring'}
                          label={'Set as recurring report'}
                        />
                      </div>
                    </div>
                    <div>
                      <TextInput
                        rules={{
                          required: 'Email is required',
                          pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
                            message: 'Invalid email address',
                          },
                        }}
                        name={'email'}
                        label={'Email'}
                        defaultValue={user?.email || ''}
                      />
                    </div>
                    <div className={'grid grid-cols-3 gap-2 flex-row'}>
                      <RadioInput
                        rules={{
                          required: 'Report format is required',
                        }}
                        label={'Report Format'}
                        options={[
                          {
                            label: 'Excel (XLSX)',
                            value: 'xlsx',
                          },
                          {
                            label: 'Zipped CSV',
                            value: 'csv',
                          },
                          {
                            label: 'Google sheet',
                            value: 'google_sheet',
                          },
                        ]}
                        name={'reportFormat'}
                      />
                      {recurring && (
                        <>
                          <RadioInput
                            onChange={() => {
                              setValue('schedule_day', null)
                            }}
                            label={'Every'}
                            options={scheduleOptions}
                            name={'schedule_frequency'}
                          />
                          {scheduleFrequency === 'monthly' && (
                            <SelectInput
                              label={'Which day of the month?'}
                              name={'schedule_day'}
                              options={scheduleDayOptions}
                            />
                          )}
                        </>
                      )}
                    </div>
                    {reportFormat === 'google_sheet' && (
                      <div className="flex flex-col gap-2">
                        <TextInput
                          name={'sheet_url'}
                          label={'Google sheet URL'}
                          rules={{ required: true }}
                        />
                        <h3 className="text-sm">
                          Share your Google sheet with this email address:{' '}
                          <strong>{service_email}</strong>
                        </h3>
                        <div className="w-fit">
                          <Button
                            variant="secondary"
                            disabled={!sheetURL}
                            onClick={async (e) => {
                              e.preventDefault()
                              const toastId = toast.loading('Checking access...')
                              if (!service_email) {
                                toast.error('Access denied!', { id: toastId })
                                return
                              }

                              await checkAccess(
                                {
                                  spreadsheet_url: sheetURL,
                                  service_email: service_email,
                                },
                                {
                                  onSuccess: () => {
                                    toast.success('Access granted!', { id: toastId })
                                  },
                                  onError: () => {
                                    toast.error('Access denied!', { id: toastId })
                                  },
                                },
                              )
                            }}>
                            Verify access
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Submission */}
                  <div className={'flex flex-col gap-3 pt-5 items-end'}>
                    <div>
                      <Button
                        disabled={isSubmitting}
                        animate={false}
                        variant={isSubmitSuccessful ? 'success' : 'rainbow'}
                        type={'submit'}>
                        {isSubmitSuccessful
                          ? 'Report sent'
                          : isSubmitting
                          ? 'Generating report...'
                          : 'Generate report'}
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </motion.form>
        </AnimatePresence>
      </FormProvider>
    </>
  )
}
